// ** MUI Imports
import { styled, useTheme } from '@mui/material/styles'
import MuiAppBar from '@mui/material/AppBar'
import MuiToolbar from '@mui/material/Toolbar'
import useMediaQuery from "@mui/material/useMediaQuery";
import { memo, useMemo } from 'react'

// ** Util Import
import { hexToRGBA } from '../../../../components/utils/hex-to-rgba'

// Move styled components outside of the component function
const AppBar = styled(MuiAppBar)(({ theme }) => ({
  transition: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
  minHeight: theme.mixins.toolbar.minHeight,
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(6)
  },
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4)
  }
}))

const Toolbar = styled(MuiToolbar)(({ theme }) => ({
  width: '100%',
  borderRadius: theme.shape.borderRadius,
  padding: `${theme.spacing(0, 6)} !important`,
}))

const LayoutAppBar = memo((props) => {
  // ** Hooks - These must be called unconditionally at the top level
  const theme = useTheme();
  const smBelow = useMediaQuery(theme.breakpoints.down("sm"));

  // ** Props
  const { settings, appBarProps, appBarContent: userAppBarContent } = props

  // ** Vars - Extract these after hooks but before any conditionals
  const { skin, appBar, appBarBlur } = settings

  // Memoized values - must be called unconditionally
  const userAppBarStyle = useMemo(() => {
    return appBarProps?.sx || {};
  }, [appBarProps]);

  const userAppBarProps = useMemo(() => {
    const propsCopy = { ...appBarProps };
    delete propsCopy.sx;
    return propsCopy;
  }, [appBarProps]);

  const toolbarSx = useMemo(() => {
    const baseSx = {
      minHeight: theme => `${theme.mixins.toolbar.minHeight}px !important`,
      backgroundColor: theme => hexToRGBA(theme.palette.background.paper, appBarBlur ? 0.95 : 1),
      marginTop: smBelow ? 0 : theme.spacing(4),
    };

    if (appBarBlur) {
      baseSx.backdropFilter = 'blur(6px)';
    }

    if (skin === 'bordered') {
      baseSx.border = theme => `1px solid ${theme.palette.divider}`;
    } else {
      baseSx.boxShadow = 2;
    }

    return baseSx;
  }, [appBarBlur, skin, smBelow, theme]);

  // Now check if appBar is hidden and return null
  if (appBar === 'hidden') {
    return null;
  }

  return (
    <AppBar
      elevation={0}
      color='default'
      className='layout-navbar'
      sx={userAppBarStyle}
      position={appBar === 'fixed' ? 'sticky' : 'static'}
      {...userAppBarProps}
    >
      <Toolbar
        className='navbar-content-container'
        sx={toolbarSx}
      >
        {(userAppBarContent && userAppBarContent(props)) || null}
      </Toolbar>
    </AppBar>
  );
});

LayoutAppBar.displayName = 'LayoutAppBar';

export default LayoutAppBar;